import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, ArrowRight, Heart, Users, Shield } from 'lucide-react';

const sections = [
  {
    id: 'aesthetic-nursing',
    icon: Heart,
    title: 'Section of Aesthetic Nursing',
    exam: 'CBENA',
    examFull: 'Competency-Based Examination for Nursing in Aesthetic Practice',
    eligibility: 'Registered Nurses with active license',
    fee: '₹22,000',
    link: '/allied-faculty/aesthetic-nursing',
    features: [
      'Procedure assistance protocols',
      'Emergency response drills',
      'Patient safety management',
      'Clinical documentation',
    ],
  },
  {
    id: 'clinical-assistance',
    icon: Users,
    title: 'Section of Clinical Aesthetic Assistance',
    exam: 'CBEAA',
    examFull: 'Competency-Based Examination for Aesthetic Assistants',
    eligibility: 'Diploma or recognized institutional training',
    fee: '₹22,000',
    link: '/allied-faculty/clinical-assistance',
    features: [
      'Tray setup & sterility protocols',
      'Clinical documentation',
      'Patient preparation',
      'Equipment management',
    ],
  },
];

export default function AlliedFacultyPage() {
  return (
    <div data-testid="allied-faculty-page">
      <section className="bg-[#1B2A4A] py-16 md:py-20">
        <div className="imcac-container">
          <p className="imcac-overline mb-3">Division of Competency-Based Examination & Certification</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight font-heading">
            Faculty of Allied Clinical Competency
          </h1>
          <div className="h-1 w-20 bg-[#C5A572] mt-5" />
          <p className="text-lg text-slate-300 mt-4 max-w-3xl">
            Competency-based certification pathways for Nurses & Clinical Assistants in aesthetic practice.
          </p>
        </div>
      </section>

      {/* Faculty Overview */}
      <section className="imcac-section bg-white">
        <div className="imcac-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <p className="imcac-overline mb-3">Faculty Overview</p>
              <h2 className="text-3xl font-semibold text-[#1B2A4A] font-heading mb-6">
                Elevating Standards for Allied Healthcare Professionals
              </h2>
              <p className="text-base text-slate-600 leading-relaxed mb-6">
                The Faculty of Allied Clinical Competency provides structured certification pathways 
                for registered nurses and clinical assistants working in aesthetic medicine settings. 
                Each examination is designed to validate competency within the specific scope of practice 
                of the respective profession.
              </p>

              <div className="bg-amber-50 border border-amber-200 rounded-sm p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Shield className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-semibold text-amber-800 mb-1">Scope of Practice Notice</p>
                    <p className="text-sm text-amber-700">
                      All certifications under this faculty are subject to national and regional scope-of-practice 
                      regulations. Certification does not authorize procedures beyond the legal scope permitted 
                      for each professional category in the candidate's jurisdiction.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <img
                src="https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&w=800&q=80"
                alt="Allied Healthcare Professionals"
                className="w-full h-64 object-cover rounded-sm shadow-md mb-6"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Two Sections */}
      <section className="imcac-section bg-[#F8FAFC]">
        <div className="imcac-container">
          <div className="text-center mb-12">
            <p className="imcac-overline mb-3">Certification Pathways</p>
            <h2 className="text-3xl font-semibold text-[#1B2A4A] font-heading">Two Specialty Sections</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {sections.map((section) => (
              <Card key={section.id} className="imcac-card border-t-4 border-t-[#C5A572]" data-testid={`section-${section.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 bg-[#1B2A4A] rounded-sm flex items-center justify-center">
                      <section.icon className="h-5 w-5 text-[#C5A572]" />
                    </div>
                    <Badge className="bg-[#C5A572] text-white text-xs">{section.exam}</Badge>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-[#1B2A4A] font-heading mb-2">{section.title}</h3>
                  <p className="text-xs text-slate-500 mb-4">{section.examFull}</p>

                  <div className="space-y-2 mb-4">
                    {section.features.map((feature, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <CheckCircle className="h-3 w-3 text-[#C5A572] flex-shrink-0" />
                        <span className="text-xs text-slate-600">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="border-t border-slate-200 pt-4 mb-4">
                    <div className="flex justify-between items-center">
                      <div>
                        <p className="text-xs text-slate-500">Eligibility</p>
                        <p className="text-sm text-[#1B2A4A] font-medium">{section.eligibility}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-slate-500">Fee (India)</p>
                        <p className="text-lg font-bold text-[#C5A572]">{section.fee}</p>
                      </div>
                    </div>
                  </div>

                  <Link to={section.link} data-testid={`explore-${section.exam.toLowerCase()}`}>
                    <Button className="w-full bg-[#1B2A4A] hover:bg-[#152039] text-white rounded-sm text-sm">
                      Explore {section.exam} <ArrowRight className="ml-2 h-3 w-3" />
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Legal Disclaimer */}
      <section className="py-10 bg-[#F8FAFC]">
        <div className="imcac-container">
          <div className="bg-white border border-slate-200 rounded-sm p-6">
            <div className="flex items-start gap-3">
              <Shield className="h-5 w-5 text-[#C5A572] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-[#1B2A4A] mb-2">Legal Disclaimer</p>
                <p className="text-sm text-slate-600 leading-relaxed">
                  Certifications issued under the Faculty of Allied Clinical Competency validate 
                  assessed competencies within the respective professional domain and do not grant medical 
                  licensure or authorize procedures beyond the candidate's professional scope of practice. 
                  All candidates must comply with the regulatory requirements of their respective professional 
                  councils (Nursing Council, etc.) in their jurisdiction.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
